import { guardUnspecified } from '@portal/utils/util-guards';
import { Regions } from '@smh/projects/regions';
import { componentFactoryOf } from 'vue-tsx-support';

import { JtnUiRecordOpinionAuthors } from '@jtn-ui/components/record';
import { JtnUiTypography } from '@jtnews/jtn-ui';
import {
  injectStylesMixin,
  DateInfo,
  ObserverVisibility,
  tsStoreMixin
} from '@jtnews/shared';
import type { BaseRecord, Format, Rubric, Theme } from '@jtnews/shared/news';

import styles from './record-header-info.styles.scss?module';

type Author = {
  id: string;
  url?: string;
  name: string;
  targetUrl?: string;
  jobPosition: string;
  image?: {
    fileName: string;
    width: number;
    height: number;
  };
};

type Record = BaseRecord & {
  publishAt?: string;
  formats?: Format[];
  authors?: Author[];
  headerKeywords?: string[];
};

interface IEvents {
  onRubricClick: string;
  onFormatClick: string;
  onThemeClick: string;
  onDateClick: void;
}

type ComponentData = {
  authorBlockVisibility: ObserverVisibility;
};

export default componentFactoryOf<IEvents>()
  .mixin(injectStylesMixin(styles))
  .mixin(tsStoreMixin)
  .create({
    name: 'RecordHeaderInfo',
    props: {
      headerData: {
        type: Object as () => Record | null,
        default: null
      },
      regionId: {
        type: Number as () => Regions,
        default: Regions.Nsk
      },
      hasSubheader: {
        type: Boolean,
        default: true
      },
      isMobileOnly: {
        type: Boolean,
        default: false
      }
    },
    data(): ComponentData {
      return {
        authorBlockVisibility: new ObserverVisibility('all')
      };
    },
    computed: {
      header(): string {
        return this?.headerData?.header ? this?.headerData?.header : '';
      },
      subheader(): string {
        return this?.headerData?.subheader ? this?.headerData?.subheader : '';
      },
      formats(): Format[] {
        return this?.headerData?.formats ? this.headerData.formats : [];
      },
      rubrics(): Rubric[] {
        return this?.headerData?.rubrics ? this.headerData.rubrics : [];
      },
      themes(): Theme[] {
        return this?.headerData?.themes ? this.headerData.themes : [];
      },
      publishAt(): string {
        return this?.headerData?.publishAt ? this.headerData.publishAt : '';
      },
      viewsCount(): string {
        return this?.headerData?.viewsCount
          ? this.headerData.viewsCount.toLocaleString('ru-RU')
          : '';
      },
      isSmallFontInMobile(): boolean {
        if (!this.isMobileOnly) {
          return false;
        }
        return this?.header.length > 140 || this.subheader.length > 160;
      },
      isOpinion(): boolean {
        return (
          guardUnspecified(this.headerData) && this.headerData.template === 'opinions'
        );
      },
      authors() {
        return this.headerData?.authors;
      },
      keywords(): string[] {
        return this.headerData?.headerKeywords ?? [];
      }
    },
    mounted() {
      this.authorBlockVisibility.event.subscribe(state => {
        if (state.value) {
          const prop1 = 'Просмотр';
          const prop2 = 'Блок вверху';
          const goalName = 'ViewAuthor';
          this.sendNewReachGoal(prop1, prop2, goalName);
        }
      });
    },
    methods: {
      emitRubricClick(rubric: string) {
        this.$emit('rubricClick', rubric);
      },
      emitFormatClick(format: string) {
        this.$emit('formatClick', format);
      },
      emitThemeClick(theme: string) {
        this.$emit('themeClick', theme);
      },
      emitDateClick() {
        this.$emit('dateClick');
      },
      getBreadcrumbsProps(index: number) {
        return index === 0
          ? {
              attrs: {
                itemProp: 'itemListElement',
                itemScope: true,
                itemType: 'http://schema.org/ListItem'
              }
            }
          : null;
      },
      sendNewReachGoal(prop1: string, prop2: string, goalName: string) {
        const blockType = 'Центральная колонка';
        const fieldType = 'Блок Автор';
        this.store.analyticsModule.sendNewReachGoal({
          blockType,
          fieldType,
          valueName: { [prop1]: prop2 },
          goalName
        });
      },
      sendAuthorClickedRG() {
        const prop1 = 'Переход на автора';
        const prop2 = 'Блок вверху';
        const goalName = 'ClickAuthor';

        this.sendNewReachGoal(prop1, prop2, goalName);
      }
    },
    render() {
      return (
        <section class={styles.recordHeaderInfo}>
          <div class={styles.infoWrap}>
            <div class={styles.group}>
              <ul
                class={styles.rubrics}
                itemScope
                itemType="http://schema.org/BreadcrumbList"
              >
                {this.rubrics.map((item, index) => (
                  <li {...this.getBreadcrumbsProps(index)}>
                    <JtnUiTypography
                      class={styles.rubricLink}
                      tag="a"
                      href={item.path}
                      title={item.name}
                      type="font-format-news"
                      link-type="link-gray"
                      itemprop={index === 0 ? 'item' : null}
                      onClick={() => this.emitRubricClick(item.name)}
                    >
                      <span itemProp={index === 0 ? 'name' : null}>{item.name}</span>
                      {index === 0 && <meta itemprop="position" content="1" />}
                    </JtnUiTypography>
                  </li>
                ))}
                <li
                  itemProp="itemListElement"
                  itemScope
                  itemType="http://schema.org/ListItem"
                >
                  <meta itemprop="name" content={this.header} />
                  <meta itemprop="position" content="2" />
                </li>
              </ul>
              {this.themes.map(item => (
                <JtnUiTypography
                  class={styles.themeLink}
                  tag="a"
                  href={item.path}
                  title={item.name}
                  type="font-vijet-type-6"
                  link-type="link-blue"
                  itemscope
                  itemprop="about"
                  itemtype="https://schema.org/Thing"
                  onClick={() => this.emitThemeClick(item.name)}
                >
                  <span itemProp="name">{item.name}</span>
                </JtnUiTypography>
              ))}

              {this.formats.map(item =>
                item.url ? (
                  <JtnUiTypography
                    class={styles.formatLink}
                    tag="a"
                    href={item.url}
                    title={item.name}
                    type="font-format-news"
                    link-type="link-gray"
                    itemscope
                    itemprop="about"
                    itemtype="https://schema.org/Thing"
                    onClick={() => this.emitFormatClick(item.name)}
                  >
                    <span itemProp="name">{item.name}</span>
                  </JtnUiTypography>
                ) : (
                  <JtnUiTypography
                    class={styles.formatLink}
                    tag="span"
                    itemscope
                    itemprop="about"
                    itemtype="https://schema.org/Thing"
                    type="font-format-news"
                  >
                    <span itemProp="name">{item.name}</span>
                  </JtnUiTypography>
                )
              )}
              {this.keywords.map(item => (
                <span itemscope itemprop="about" itemtype="https://schema.org/Thing">
                  <meta itemProp="name" content={item} />
                </span>
              ))}
            </div>

            <div class={styles.recordBody}>
              <JtnUiTypography
                class={[styles.header, this.isSmallFontInMobile ? styles.smallFont : '']}
                tag="h1"
                type="font-center-title"
                link-type="link-dark-type-2"
                itemprop="headline"
              >
                <span domPropsInnerHTML={this.header} />
              </JtnUiTypography>
              {this.subheader && this.hasSubheader && (
                <JtnUiTypography
                  class={styles.subheader}
                  tag="p"
                  type="font-second-news-txt"
                  itemprop="alternativeHeadline"
                >
                  <span domPropsInnerHTML={this.subheader} />
                </JtnUiTypography>
              )}
            </div>
            {this.isOpinion && (
              <JtnUiRecordOpinionAuthors
                v-observe-visibility={this.authorBlockVisibility.getOptions('all')}
                class={styles.author}
                isHeader={true}
                authors={this.authors}
                onClickedAuthor={() => this.sendAuthorClickedRG()}
              />
            )}
            <div class={styles.recordDetails}>
              {this.publishAt && (
                <JtnUiTypography
                  class={styles.publishDate}
                  tag="div"
                  type="font-photoday-type-1"
                  datetime={this.publishAt}
                  itemprop="datePublished"
                  onCklic={() => this.emitDateClick()}
                >
                  <DateInfo
                    date={this.publishAt}
                    regionId={this.regionId}
                    isWithLink={true}
                  />
                </JtnUiTypography>
              )}
              {this.viewsCount && (
                <JtnUiTypography
                  class={styles.viewCount}
                  tag="span"
                  itemprop="item"
                  type="font-format-news"
                >
                  <svg class={styles.eyeIcon} width={16} height={16} stroke-width={1.5}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-eye-small"></use>
                  </svg>
                  <span>{this.viewsCount}</span>
                </JtnUiTypography>
              )}
            </div>
          </div>
        </section>
      );
    }
  });
